import { useEffect, useRef } from 'react';
import { getAuthAdapter } from '../auth/createAuthAdapter';

const DEFAULT_THRESHOLD = 0.75;
// Reuse the existing auth broadcast channel so token-lifecycle events
// (logout, proactive refresh) are all coordinated on a single channel.
const AUTH_CHANNEL = 'mdvip-auth';

interface UseProactiveTokenRefreshOptions {
  /** Fraction of token lifetime consumed before refresh is triggered (0–1). Defaults to 0.75. */
  threshold?: number;
  /** Refresh only runs while the user is authenticated. */
  isEnabled: boolean;
}

/**
 * Web-only hook that silently refreshes the access token when the user returns
 * to the tab and a configurable fraction of the token's lifetime has elapsed.
 *
 * Cross-tab coordination:
 * - Before refreshing, posts `{ type: 'token_refresh_starting' }` so other tabs
 *   can skip their own refresh when they gain focus.
 * - Listens for `token_refreshed` from other tabs and updates in-memory expiry
 *   refs, which prevents a redundant refresh call from this tab.
 *
 * Mount from IdleTimeoutManagerWeb so it lives alongside the existing
 * idle-timeout side-effect logic without needing its own provider.
 */
export function useProactiveTokenRefresh({
  threshold = DEFAULT_THRESHOLD,
  isEnabled,
}: UseProactiveTokenRefreshOptions): void {
  const expiresAtRef = useRef<number | null>(null);
  const issuedAtRef = useRef<number | null>(null);
  // Set to true when another tab broadcasts that it is already handling a refresh.
  // Cleared after the visibility event resolves so subsequent focus events still work.
  const skipNextRefreshRef = useRef(false);
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;

  // Initialise in-memory refs from storage on mount / when auth state changes.
  useEffect(() => {
    if (!isEnabled) {
      expiresAtRef.current = null;
      issuedAtRef.current = null;
      return;
    }
    const adapter = getAuthAdapter();
    void Promise.all([adapter.getTokenExpiresAt(), adapter.getTokenIssuedAt()]).then(([expiresAt, issuedAt]) => {
      expiresAtRef.current = expiresAt;
      issuedAtRef.current = issuedAt;
    });
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled) return;

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL);
    } catch {
      // BroadcastChannel unavailable (e.g. non-browser test environment)
    }

    // Listen for cross-tab token lifecycle messages.
    if (channel) {
      channel.onmessage = ({ data }: MessageEvent<{ type: string; expiresAt?: number; issuedAt?: number }>) => {
        if (data.type === 'token_refresh_starting') {
          // Another tab is already handling a refresh — skip ours.
          skipNextRefreshRef.current = true;
        } else if (data.type === 'token_refreshed' && data.expiresAt !== undefined && data.issuedAt !== undefined) {
          // Another tab completed a refresh — update our in-memory state so
          // the next focus check sees a fresh token.
          expiresAtRef.current = data.expiresAt;
          issuedAtRef.current = data.issuedAt;
          skipNextRefreshRef.current = false;
        }
      };
    }

    function shouldRefresh(
      expiresAt: number | null,
      issuedAt: number | null,
      refreshThreshold: number,
      skipFlag: { current: boolean },
    ): boolean {
      if (expiresAt === null || issuedAt === null) return false;
      if (skipFlag.current) {
        skipFlag.current = false;
        return false;
      }
      const totalLifetime = expiresAt - issuedAt;
      if (totalLifetime <= 0) return false;
      const consumed = (Date.now() - issuedAt) / totalLifetime;
      return consumed >= refreshThreshold;
    }

    const handleVisibilityChange = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      if (!shouldRefresh(expiresAtRef.current, issuedAtRef.current, thresholdRef.current, skipNextRefreshRef)) {
        return;
      }

      channel?.postMessage({ type: 'token_refresh_starting' });

      try {
        const adapter = getAuthAdapter();
        await adapter.refresh();
        // Read the updated timestamps written by the adapter after refresh.
        const [expiresAt, issuedAt] = await Promise.all([adapter.getTokenExpiresAt(), adapter.getTokenIssuedAt()]);
        expiresAtRef.current = expiresAt;
        issuedAtRef.current = issuedAt;
      } catch (err) {
        console.warn('Proactive token refresh failed:', err);
      }
    };

    // Stable wrapper so addEventListener/removeEventListener reference the same function.
    const onVisibilityChange = (): void => {
      void handleVisibilityChange();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (channel) {
        channel.onmessage = null;
        channel.close();
      }
    };
  }, [isEnabled]);
}
